// 深链目标会话（widget.html ?view=chat&chat_id=N → Chat.props.initialChatId）
// 相关的纯判定逻辑——抽出来是因为它们本来就该跟 chat.js 里一堆有状态、有副作用
// 的方法分开：这两条判定不碰 DOM/state/网络，纯输入→输出，可以直接 node --test。
//
// 背景：deep-link 修复之前，Chat 组件先按服务端 /v1/chat 挑的默认会话完整走一次
// 首屏渲染+loadMessages，再由 Widget._applyInitialChatId 用 retry 轮询等 Chat
// 实例挂载好，imperative 调 switchToChat() 二次切换——用户先看一眼错误会话，
// 多打一次 load-messages。这次改成把 initialChatId 原样透传进 Chat，让它在
// componentDidMount 的第一次 _applyInitResponse 里就决定"这次到底该用哪个
// chat_id"，第一次 loadMessages 直接打对目标。resolveInitialChatId 就是那个
// 决定；shouldFallBackToDefaultChat 是深链目标不属于本访客（服务端 404）时
// "该不该回退到默认会话重试"的判定。

/**
 * 挂载后第一次收到 /v1/chat 响应时，这次该用深链目标还是服务端默认会话。
 *
 * 只在"第一次" (alreadyConsumed === false) 且深链目标真的存在时才用深链目标——
 * 之后（用户手动切换会话、preInit 失败重试等场景 _applyInitResponse 被再次
 * 调用）一律尊重服务端这次给的默认值，深链目标不会被反复"抢焦点"。
 *
 * @param {object} params
 * @param {number|string|null|undefined} params.initialChatId props.initialChatId
 *   （已经在 widget-index.js 用 parsePositiveIntId 校验过，这里不重复校验）
 * @param {boolean} params.alreadyConsumed 深链目标是否已经被用过一次
 * @param {number|string|null|undefined} params.defaultChatId 服务端 /v1/chat
 *   响应里的 chatId（本次响应挑的默认会话）
 * @returns {number|string|null|undefined} 本次应该使用的 chat_id
 */
export function resolveInitialChatId({ initialChatId, alreadyConsumed, defaultChatId }) {
    if (!alreadyConsumed && initialChatId) {
        return initialChatId;
    }
    return defaultChatId;
}

/**
 * loadMessages 对当前 chat_id 返回"不属于本访客"（服务端 404，见
 * ApiController::loadMessages 的 client_id 归属校验）时，该不该回退到默认会话
 * 重新拉一次。只有"当前目标确实不是默认会话，且默认会话确实存在"才回退——
 * 否则要么本来就是在请求默认会话（不该无限重试），要么压根没有默认会话可退
 * （异常状态，不强行造一个）。
 *
 * @param {object} params
 * @param {number|string|null|undefined} params.currentChatId 刚刚请求失败的 chat_id
 * @param {number|string|null|undefined} params.defaultChatId 服务端默认会话
 * @returns {boolean}
 */
export function shouldFallBackToDefaultChat({ currentChatId, defaultChatId }) {
    return !!defaultChatId && currentChatId !== defaultChatId;
}
